import React, { useState } from "react";
import {
  Card,
  Image,
  Text,
  Badge,
  Button,
  Group,
  Stack,
  ActionIcon,
  Box,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "@tabler/icons-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useTranslation } from "../context/TranslationContext";
import { formatWeight } from "../utils/formatWeight";
import { formatPrice } from "../utils/formatPrice";
import { localizedField } from "../utils/localizedField";
import { effectiveCustomerPrice } from "../utils/adminCustomerPrice";
import { offerLabel } from "../utils/offerLabel";
import { isVariantSoldOut } from "../utils/soldOut";

export default function ProductCard({ product, previewMode = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart, cartItems, updateQuantity, removeFromCart } = useCart();

  const [addingToCart, setAddingToCart] = useState(false);

  // Parent product has nested variants; default variant is first or marked is_default
  const defaultVariant =
    product.variants?.find((v) => v.is_default) || product.variants?.[0] || null;
  const visibleVariants = (product.variants || []).filter(
    (v) => !v.is_hidden
  );

  // A product with no visible variants has nothing to add to the cart.
  const hasNoVariant = visibleVariants.length === 0;
  const isBulkOnly = product.is_bulk_only;

  // Sold-out (gated by can_preorder): a preorder variant never counts as sold out
  const isSoldOut =
    hasNoVariant || visibleVariants.every((v) => isVariantSoldOut(v));
  const isDefaultVariantSoldOut = hasNoVariant || isVariantSoldOut(defaultVariant);

  // Cart logic — match by default variant SKU
  const cartItem = !previewMode
    ? cartItems.find((item) => item.sku === defaultVariant?.sku)
    : null;
  const cartQuantity = cartItem?.quantity || 0;
  const inCart = cartQuantity > 0;

  // Preorder variants may exceed stock (soft-block / committed-stock model);
  // non-preorder variants are hard-capped at their available stock.
  const stockQuantity = defaultVariant?.stock_quantity ?? 0;
  const canIncreaseQuantity =
    Number(defaultVariant?.can_preorder) !== 0 ||
    cartQuantity < stockQuantity;

  const handleAddToCart = previewMode ? undefined : async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) return navigate("/login");
    if (!defaultVariant) return;

    try {
      setAddingToCart(true);
      await addToCart({ ...defaultVariant, name: product.name }, 1);
    } catch (error) {
      modals.open({
        title: "Error",
        centered: true,
        children: <Text size="sm">{error.message}</Text>,
      });
    } finally {
      setAddingToCart(false);
    }
  };

  const handleIncrease = previewMode ? undefined : (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateQuantity(cartItem.cartItemId, cartQuantity + 1).catch((err) =>
      console.error(err)
    );
  };

  const handleDecrease = previewMode ? undefined : (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (cartQuantity === 1) {
      removeFromCart(cartItem.cartItemId);
    } else {
      updateQuantity(cartItem.cartItemId, cartQuantity - 1).catch((err) =>
        console.error(err)
      );
    }
  };

  // Content helpers
  const productName = localizedField(product, 'name', t.langCode);
  const productDesc = localizedField(product, 'description', t.langCode);
  const firstParagraph = productDesc.match(/<p>(.*?)<\/p>/)?.[1] || productDesc;
  const shortDesc = firstParagraph.replace(/<[^>]*>/g, '');

  // Link to the product page, pre-selecting the variant this card shows.
  const detailsUrl = defaultVariant?.sku
    ? `/products/${product.product_code}?variant=${encodeURIComponent(defaultVariant.sku)}`
    : `/products/${product.product_code}`;

  const storePrice = defaultVariant?.price ?? null;
  const retailPrice = defaultVariant?.originalPrice ?? storePrice;
  const customerPrice = defaultVariant?.customerPrice || null;
  const effectivePrice = customerPrice
    ? effectiveCustomerPrice(defaultVariant, customerPrice, cartQuantity)
    : null;
  const hasEffectivePrice = effectivePrice != null && Number.isFinite(Number(effectivePrice));
  const price = hasEffectivePrice ? Number(effectivePrice) : storePrice;
  const hasSpecialPrice = hasEffectivePrice
    ? Math.abs(Number(price) - Number(retailPrice)) >= 0.005
    : Boolean(defaultVariant?.hasSpecialPrice);
  const originalPrice = hasSpecialPrice ? retailPrice : null;
  // Prices are exposed GROSS; business users (vatExclusiveDisplay) swap to NET.
  const vatExclusive = Boolean(defaultVariant?.vatExclusiveDisplay);
  const hasNetPrice = defaultVariant?.netPrice != null;
  const displayPrice = price != null
    ? (vatExclusive
        ? (hasEffectivePrice ? Number(effectivePrice) : hasNetPrice ? Number(defaultVariant.netPrice) : Number(price) / (1 + Number(defaultVariant?.vatRate || 0) / 100))
        : Number(price))
    : null;
  const displayOriginal = originalPrice != null
    ? (vatExclusive ? Number(originalPrice) / (1 + Number(defaultVariant?.vatRate || 0) / 100) : Number(originalPrice))
    : null;

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        overflow: "visible",
        opacity: isSoldOut ? 0.7 : 1,
      }}
    >
      {/* --- Start of IMAGE SECTION --- */}
      <Box
        component={previewMode ? undefined : Link}
        to={previewMode ? undefined : detailsUrl}
        pos="relative"
        onClick={(e) => e.stopPropagation()}
      >
        <Image
          src={product.image}
          height={200}
          alt={productName}
          radius="md"
          fallbackSrc="https://placehold.co/400x400?text=No+Image"
        />

        {/* 1. TOP LEFT — Offer ribbon (corner sticker) */}
        {product.offer && (
          <Badge
            pos="absolute"
            top={-17}
            left={-17}
            color={
              product.offer.type === 'free_shipping' ? 'teal'
              : product.offer.type === 'buy_x_pay_y' ? 'orange'
              : product.offer.type === 'free_nth_order' ? 'violet'
              : 'red'
            }
            variant="filled"
            radius="xl"
            style={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: "rotate(-30deg)",
              padding: 0,
              fontSize: "1rem",
              color: "white",
              lineHeight: 1.1,
              whiteSpace: "normal",
              textAlign: "center",
              opacity: 0.8,
              pointerEvents: "none",
              zIndex: 2,
            }}
            styles={{
              label: {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflow: 'hidden',
                textOverflow: 'clip',
                padding: '0 10px',
              },
            }}
          >
            {offerLabel(product.offer, t)}
          </Badge>
        )}

        {/* 2. TOP RIGHT STACK — price, strikethrough, sold-out */}
        {((price != null && !isBulkOnly) || isSoldOut) && (
          <Box
            pos="absolute"
            top={10}
            right={10}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 5,
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            {isSoldOut && (
              <Badge
                color="#dc3545"
                variant="filled"
                size="lg"
                style={{
                  border: "2px solid white",
                  fontWeight: 800,
                }}
              >
                {t("soldOut", "SOLD OUT")}
              </Badge>
            )}
            {displayPrice != null && !isBulkOnly && (
              <Badge
                variant="filled"
                color={hasSpecialPrice ? "red" : "blue"}
                size="lg"
                shadow="md"
                style={{
                  border: "2px solid white",
                  fontWeight: 800,
                  lineHeight: 1.15,
                  height: 33,
                  alignContent: "center",
                }}
              >
                <Box style={{ textAlign: "right" }}>
                  <span>{formatPrice(displayPrice)}</span>
                  <span style={{ display: "block", fontSize: "0.6em", fontWeight: 600, opacity: 0.85 }}>
                    {vatExclusive ? t("exclVAT", "excl. VAT") : t("inclVAT", "incl. VAT")}
                  </span>
                </Box>
              </Badge>
            )}
            {hasSpecialPrice && displayOriginal != null && !isBulkOnly && (
              <Badge
                variant="light"
                color="gray"
                size="sm"
                style={{
                  border: "2px solid white",
                  textDecoration: "line-through",
                  opacity: 0.8,
                }}
              >
                {formatPrice(displayOriginal)}
              </Badge>
            )}
          </Box>
        )}
      </Box>{" "}
      {/* 2. INFO SECTION */}
      <Stack justify="space-between" mt="md" style={{ flexGrow: 1 }}>
        <Box>
          {/* <Text size="xs" color="dimmed" fw={700} tt="uppercase" lts={1}>
            {product.category}
          </Text> */}

          <Group gap="xs" align="center" mt={4} wrap="nowrap">
            <Text fw={600} size="lg" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
              {productName}
            </Text>
            {product.is_variable_weight && (
              <Badge
                size="xs"
                color="yellow"
                variant="light"
                radius="sm"
                style={{ flexShrink: 0 }}
              >
                {t('soldByWeightShort', 'Sold by weight')}
              </Badge>
            )}
          </Group>

          <Text size="sm" color="dimmed" mt={4} lineClamp={2}>
            {shortDesc}
          </Text>
        </Box>

        {/* 3. FOOTER SECTION */}
        <Box>
          <Group justify="space-between" mb="md">
            {defaultVariant?.weight_value != null && (
              <Text size="sm" fw={500} color="gray.7">
                <Icons.IconPackage
                  size={16}
                  aria-hidden="true"
                  style={{ verticalAlign: "middle", marginRight: 4 }}
                />
                {formatWeight(defaultVariant.weight_value, defaultVariant.weight_unit)}
                {visibleVariants.length > 1 && (
                  <Text component="span" size="xs" c="dimmed" ml={4}>
                    ({visibleVariants.length} options)
                  </Text>
                )}
              </Text>
            )}
          </Group>

          <Group gap={8}>
            <Button
              component={previewMode ? undefined : Link}
              to={previewMode ? undefined : detailsUrl}
              variant="light"
              color="gray"
              style={{ flex: 1 }}
              size="sm"
              onClick={(e) => e.stopPropagation()}
              tabIndex={previewMode ? -1 : undefined}
            >
              {t("viewDetails", "Details")}
            </Button>

            {!isDefaultVariantSoldOut && !isBulkOnly && (
              <Box style={{ flex: 1.5 }}>
                {previewMode ? (
                  <Button
                    fullWidth
                    size="sm"
                    leftSection={<Icons.IconShoppingCartPlus size={18} aria-hidden="true" />}
                    tabIndex={-1}
                  >
                    {t("addToCart", "Add")}
                  </Button>
                ) : inCart ? (
                  <Group gap={5} grow>
                    <ActionIcon
                      variant="outline"
                      color="blue"
                      onClick={handleDecrease}
                      size="lg"
                      aria-label={t("decreaseQuantity", `Decrease quantity of ${productName}`)}
                    >
                      <Icons.IconMinus size={16} aria-hidden="true" />
                    </ActionIcon>

                    <Text fw={700} ta="center" size="sm" aria-label={t("currentQuantity", `{n} in cart`, { n: cartQuantity })}>
                      {cartQuantity}
                    </Text>

                    <ActionIcon
                      variant="outline"
                      color="blue"
                      onClick={handleIncrease}
                      size="lg"
                      disabled={!canIncreaseQuantity}
                      aria-label={t("increaseQuantity", `Increase quantity of ${productName}`)}
                    >
                      <Icons.IconPlus size={16} aria-hidden="true" />
                    </ActionIcon>
                  </Group>
                ) : (
                  <Button
                    fullWidth
                    size="sm"
                    loading={addingToCart}
                    leftSection={<Icons.IconShoppingCartPlus size={18} aria-hidden="true" />}
                    onClick={handleAddToCart}
                  >
                    {t("addToCart", "Add")}
                  </Button>
                )}
              </Box>
            )}
          </Group>
        </Box>
      </Stack>
    </Card>
  );
}
